import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles,
  Box,
  Chip,
  TextField,
  MenuItem,
  Button,
  LinearProgress,
} from '@material-ui/core';
import { EmptyState } from '@backstage/core-components';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PromResult {
  metric: Record<string, string>;
  value?: [number, string];
  values?: [number, string][];
}

interface TimeSeriesPoint {
  time: string;
  ts: number;
  [key: string]: string | number;
}

interface XViewPoint {
  time: number;
  duration: number;
  traceID: string;
  spanName: string;
  status: 'ok' | 'error';
}

interface SpanSummary {
  spanName: string;
  rps: number;
  errorRate: number;
  p50: number;
  p95: number;
  p99: number;
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles(theme => ({
  metricCard: {
    textAlign: 'center',
    padding: theme.spacing(1.5),
    height: '100%',
    borderRadius: 8,
  },
  metricValue: {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  errorText: { color: theme.palette.error.main },
  successText: { color: '#4caf50' },
  warnText: { color: '#ff9800' },
}));

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const LOOKBACK_OPTIONS = [
  { value: '15m', label: '15분' },
  { value: '30m', label: '30분' },
  { value: '1h', label: '1시간' },
  { value: '3h', label: '3시간' },
  { value: '6h', label: '6시간' },
];

function fmtDuration(ms: number): string {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}µs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function fmtRps(v: number): string {
  if (v < 0.01) return '< 0.01';
  if (v < 1) return v.toFixed(2);
  return v.toFixed(1);
}

function fmtPercent(v: number): string {
  return `${v.toFixed(1)}%`;
}

function fmtTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const COLORS = [
  '#1976d2',
  '#d32f2f',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#c2185b',
  '#455a64',
];

/* ------------------------------------------------------------------ */
/*  Prometheus query hook                                              */
/* ------------------------------------------------------------------ */

function usePromQuery(
  fetchApi: { fetch: typeof fetch },
  query: string,
  enabled: boolean,
) {
  const [data, setData] = React.useState<PromResult[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !query) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchApi
      .fetch(
        `/api/proxy/prometheus/api/v1/query?query=${encodeURIComponent(query)}`,
      )
      .then(r => r.json())
      .then(d => {
        if (!cancelled) setData(d.data?.result ?? []);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchApi, query, enabled]);

  return { data, loading };
}

function usePromRangeQuery(
  fetchApi: { fetch: typeof fetch },
  query: string,
  lookback: string,
  step: string,
  enabled: boolean,
) {
  const [data, setData] = React.useState<PromResult[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !query) return undefined;
    let cancelled = false;
    setLoading(true);
    const now = Math.floor(Date.now() / 1000);
    const match = lookback.match(/^(\d+)([mh])$/);
    const secs = match
      ? parseInt(match[1], 10) * (match[2] === 'h' ? 3600 : 60)
      : 3600;
    const start = now - secs;
    const url = `/api/proxy/prometheus/api/v1/query_range?query=${encodeURIComponent(query)}&start=${start}&end=${now}&step=${step}`;
    fetchApi
      .fetch(url)
      .then(r => r.json())
      .then(d => {
        if (!cancelled) setData(d.data?.result ?? []);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchApi, query, lookback, step, enabled]);

  return { data, loading };
}

/* ------------------------------------------------------------------ */
/*  Tempo search hook (X-View)                                         */
/* ------------------------------------------------------------------ */

function useTempoSearch(
  fetchApi: { fetch: typeof fetch },
  serviceName: string,
  lookback: string,
  enabled: boolean,
) {
  const [data, setData] = React.useState<XViewPoint[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !serviceName) return undefined;
    let cancelled = false;
    setLoading(true);
    const now = Math.floor(Date.now() / 1000);
    const match = lookback.match(/^(\d+)([mh])$/);
    const secs = match
      ? parseInt(match[1], 10) * (match[2] === 'h' ? 3600 : 60)
      : 3600;
    const start = now - secs;
    const q = encodeURIComponent(`{resource.service.name="${serviceName}" && kind=server}`);
    const url = `/api/proxy/tempo/api/search?q=${q}&limit=500&start=${start}&end=${now}`;
    fetchApi
      .fetch(url)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const traces = d.traces ?? [];
        const points: XViewPoint[] = traces.map((t: any) => ({
          time: Math.floor(parseInt(t.startTimeUnixNano, 10) / 1e9),
          duration: (t.durationMs ?? parseInt(t.durationNanos ?? '0', 10) / 1e6),
          traceID: t.traceID ?? '',
          spanName: t.rootTraceName ?? '',
          status: t.statusCode === 2 || t.statusCode === 'STATUS_CODE_ERROR' ? 'error' as const : 'ok' as const,
        }));
        setData(points);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fetchApi, serviceName, lookback, enabled]);

  return { data, loading };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ServiceDetailCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);

  const ns =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const serviceName = entity?.metadata?.name ?? '';
  // Pod name prefix override — entity name ≠ Pod prefix 케이스 (arcus-dev ↔ arcus-portal-*)
  const podPrefix =
    entity?.metadata?.annotations?.['arcus.dev/pod-prefix'] ?? serviceName;
  // OTel service.name override — entity name ≠ Tempo service.name 케이스
  // 표준: ops/docs/design/14-catalog-tab-standard.md §5
  const traceServiceName =
    entity?.metadata?.annotations?.['arcus.dev/trace-service-name'] ?? serviceName;
  const entityKind = entity?.kind ?? '';
  const isSystem = entityKind === 'System';

  const [lookback, setLookback] = React.useState('1h');

  // namespace + service filter for queries
  const nsFilter = React.useMemo(() => {
    if (!ns) return '';
    let filter: string;
    if (isSystem && !ns.endsWith('-dev')) {
      filter = `service_namespace=~"${ns}(-dev)?"`;
    } else {
      filter = `service_namespace="${ns}"`;
    }
    // Component/Resource: 해당 서비스만 필터링
    if (!isSystem && serviceName) {
      filter += `,service="${serviceName}"`;
    }
    return filter;
  }, [ns, isSystem, serviceName]);

  const enabled = !!nsFilter;

  // ---- Instant queries: RED summary ----
  const { data: rpsData, loading: rpsLoading } = usePromQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (service)`,
    enabled,
  );

  const { data: errorData } = usePromQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER",status_code="STATUS_CODE_ERROR"}[5m])) by (service) / sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (service) * 100`,
    enabled,
  );

  const { data: latencyData } = usePromQuery(
    fetchApi,
    `histogram_quantile(0.95, sum(rate(traces_spanmetrics_latency_bucket{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (le, service))`,
    enabled,
  );

  // ---- Per-API summary ----
  const { data: apiRpsData } = usePromQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (span_name)`,
    enabled,
  );

  const { data: apiErrorData } = usePromQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER",status_code="STATUS_CODE_ERROR"}[5m])) by (span_name) / sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (span_name) * 100`,
    enabled,
  );

  const { data: apiP50Data } = usePromQuery(
    fetchApi,
    `histogram_quantile(0.50, sum(rate(traces_spanmetrics_latency_bucket{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (le, span_name))`,
    enabled,
  );

  const { data: apiP95Data } = usePromQuery(
    fetchApi,
    `histogram_quantile(0.95, sum(rate(traces_spanmetrics_latency_bucket{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (le, span_name))`,
    enabled,
  );

  const { data: apiP99Data } = usePromQuery(
    fetchApi,
    `histogram_quantile(0.99, sum(rate(traces_spanmetrics_latency_bucket{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (le, span_name))`,
    enabled,
  );

  // ---- Range query: RPS timeline ----
  const step = lookback === '15m' || lookback === '30m' ? '30s' : '60s';
  const { data: rpsTimeline, loading: timelineLoading } = usePromRangeQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (service)`,
    lookback,
    step,
    enabled,
  );

  const { data: errorTimeline } = usePromRangeQuery(
    fetchApi,
    `sum(rate(traces_spanmetrics_calls_total{${nsFilter},span_kind="SPAN_KIND_SERVER",status_code="STATUS_CODE_ERROR"}[5m])) by (service)`,
    lookback,
    step,
    enabled,
  );

  const { data: latencyTimeline } = usePromRangeQuery(
    fetchApi,
    `histogram_quantile(0.95, sum(rate(traces_spanmetrics_latency_bucket{${nsFilter},span_kind="SPAN_KIND_SERVER"}[5m])) by (le, service))`,
    lookback,
    step,
    enabled,
  );

  // ---- X-View: individual spans from Tempo ----
  const { data: xviewData, loading: xviewLoading } = useTempoSearch(
    fetchApi,
    traceServiceName,
    lookback,
    enabled && !isSystem,
  );

  // ---- JVM Metrics (OTel Java Agent) ----
  const jvmFilter = React.useMemo(() => {
    if (!serviceName || isSystem) return '';
    return `service_name="${serviceName}"`;
  }, [serviceName, isSystem]);

  const { data: threadTimeline } = usePromRangeQuery(
    fetchApi,
    jvmFilter ? `sum by (jvm_thread_state) (jvm_thread_count{${jvmFilter}})` : '',
    lookback,
    step,
    enabled && !!jvmFilter,
  );

  const { data: heapTimeline } = usePromRangeQuery(
    fetchApi,
    jvmFilter ? `sum(jvm_memory_used_bytes{${jvmFilter},jvm_memory_type="heap"}) / 1024 / 1024` : '',
    lookback,
    step,
    enabled && !!jvmFilter,
  );

  const { data: gcTimeline } = usePromRangeQuery(
    fetchApi,
    jvmFilter ? `sum(rate(jvm_gc_duration_seconds_sum{${jvmFilter}}[5m])) * 1000` : '',
    lookback,
    step,
    enabled && !!jvmFilter,
  );

  const { data: cpuTimeline } = usePromRangeQuery(
    fetchApi,
    jvmFilter ? `jvm_cpu_recent_utilization_ratio{${jvmFilter}} * 100` : '',
    lookback,
    step,
    enabled && !!jvmFilter,
  );

  // ---- Infrastructure Metrics (Pod/CPU/Memory/Network) ----
  const infraFilter = React.useMemo(() => {
    if (!ns || isSystem) return '';
    return `namespace="${ns}",pod=~"${podPrefix}.*",container!=""`;
  }, [ns, isSystem, podPrefix]);

  const infraPodFilter = React.useMemo(() => {
    if (!ns || isSystem) return '';
    return `namespace="${ns}",pod=~"${podPrefix}.*"`;
  }, [ns, isSystem, podPrefix]);

  const infraEnabled = enabled && !!infraFilter;

  const { data: podStatusData } = usePromQuery(
    fetchApi,
    infraPodFilter ? `kube_pod_status_phase{${infraPodFilter}}` : '',
    infraEnabled,
  );

  const { data: infraCpuTimeline } = usePromRangeQuery(
    fetchApi,
    infraFilter ? `sum(rate(container_cpu_usage_seconds_total{${infraFilter}}[5m])) by (pod)` : '',
    lookback,
    step,
    infraEnabled,
  );

  const { data: infraMemTimeline } = usePromRangeQuery(
    fetchApi,
    infraFilter ? `sum(container_memory_working_set_bytes{${infraFilter}}) by (pod) / 1024 / 1024` : '',
    lookback,
    step,
    infraEnabled,
  );

  const { data: infraNetRxTimeline } = usePromRangeQuery(
    fetchApi,
    infraPodFilter ? `sum(rate(container_network_receive_bytes_total{${infraPodFilter}}[5m])) by (pod)` : '',
    lookback,
    step,
    infraEnabled,
  );

  const { data: infraNetTxTimeline } = usePromRangeQuery(
    fetchApi,
    infraPodFilter ? `sum(rate(container_network_transmit_bytes_total{${infraPodFilter}}[5m])) by (pod)` : '',
    lookback,
    step,
    infraEnabled,
  );

  // ---- Service dependencies ----
  const { data: depsData } = usePromQuery(
    fetchApi,
    `sum(rate(traces_service_graph_request_total{client_service_namespace=~"${ns}.*"${!isSystem && serviceName ? `,client="${serviceName}"` : ''}}[5m])) by (client, server)`,
    enabled,
  );

  // ---- Derived data ----

  // RED summary per service
  const totalRps = rpsData.reduce(
    (sum, r) => sum + parseFloat(r.value?.[1] ?? '0'),
    0,
  );
  const avgErrorRate =
    errorData.length > 0
      ? errorData.reduce(
          (sum, r) => sum + parseFloat(r.value?.[1] ?? '0'),
          0,
        ) / errorData.length
      : 0;
  const avgP95 =
    latencyData.length > 0
      ? latencyData.reduce(
          (sum, r) => sum + parseFloat(r.value?.[1] ?? '0'),
          0,
        ) / latencyData.length
      : 0;

  // Per-API table
  const apiSummaries: SpanSummary[] = React.useMemo(() => {
    const map = new Map<string, SpanSummary>();
    apiRpsData.forEach(r => {
      const name = r.metric.span_name || 'unknown';
      map.set(name, {
        spanName: name,
        rps: parseFloat(r.value?.[1] ?? '0'),
        errorRate: 0,
        p50: 0,
        p95: 0,
        p99: 0,
      });
    });
    apiErrorData.forEach(r => {
      const name = r.metric.span_name || 'unknown';
      const entry = map.get(name);
      if (entry) entry.errorRate = parseFloat(r.value?.[1] ?? '0');
    });
    apiP50Data.forEach(r => {
      const name = r.metric.span_name || 'unknown';
      const entry = map.get(name);
      if (entry) entry.p50 = parseFloat(r.value?.[1] ?? '0');
    });
    apiP95Data.forEach(r => {
      const name = r.metric.span_name || 'unknown';
      const entry = map.get(name);
      if (entry) entry.p95 = parseFloat(r.value?.[1] ?? '0');
    });
    apiP99Data.forEach(r => {
      const name = r.metric.span_name || 'unknown';
      const entry = map.get(name);
      if (entry) entry.p99 = parseFloat(r.value?.[1] ?? '0');
    });
    return Array.from(map.values())
      .filter(s => s.rps > 0.001)
      .sort((a, b) => b.rps - a.rps);
  }, [apiRpsData, apiErrorData, apiP50Data, apiP95Data, apiP99Data]);

  // Timeline chart data
  const timelineData: TimeSeriesPoint[] = React.useMemo(() => {
    if (rpsTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    rpsTimeline.forEach(r => {
      const svc = r.metric.service || 'total';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) {
          timeMap.set(ts, { time: fmtTime(ts), ts });
        }
        const point = timeMap.get(ts)!;
        point[`rps_${svc}`] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [rpsTimeline]);

  const latencyTimelineData: TimeSeriesPoint[] = React.useMemo(() => {
    if (latencyTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    latencyTimeline.forEach(r => {
      const svc = r.metric.service || 'total';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) {
          timeMap.set(ts, { time: fmtTime(ts), ts });
        }
        const point = timeMap.get(ts)!;
        point[`p95_${svc}`] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [latencyTimeline]);

  const errorTimelineData: TimeSeriesPoint[] = React.useMemo(() => {
    if (errorTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    errorTimeline.forEach(r => {
      const svc = r.metric.service || 'total';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) {
          timeMap.set(ts, { time: fmtTime(ts), ts });
        }
        const point = timeMap.get(ts)!;
        point[`err_${svc}`] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [errorTimeline]);

  // Service names from timeline
  const serviceNames = React.useMemo(() => {
    return rpsTimeline.map(r => r.metric.service || 'total');
  }, [rpsTimeline]);

  // X-View scatter data
  const xviewOk = React.useMemo(
    () => xviewData.filter(p => p.status === 'ok'),
    [xviewData],
  );
  const xviewErr = React.useMemo(
    () => xviewData.filter(p => p.status === 'error'),
    [xviewData],
  );

  // JVM thread timeline
  const threadData: TimeSeriesPoint[] = React.useMemo(() => {
    if (threadTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    threadTimeline.forEach(r => {
      const state = (r.metric.jvm_thread_state || r.metric.state || 'unknown').toUpperCase();
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![state] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [threadTimeline]);

  // JVM heap timeline
  const heapData: TimeSeriesPoint[] = React.useMemo(() => {
    if (heapTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    heapTimeline.forEach(r => {
      const id = r.metric.id || r.metric.pool || 'heap';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![id] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [heapTimeline]);

  // JVM GC + CPU combined timeline
  const jvmMiscData: TimeSeriesPoint[] = React.useMemo(() => {
    const timeMap = new Map<number, TimeSeriesPoint>();
    gcTimeline.forEach(r => {
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)!.gcMs = parseFloat(val);
      });
    });
    cpuTimeline.forEach(r => {
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)!.cpuPct = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [gcTimeline, cpuTimeline]);

  // Dependencies
  const deps = React.useMemo(() => {
    return depsData
      .map(r => ({
        client: r.metric.client || '?',
        server: r.metric.server || '?',
        rps: parseFloat(r.value?.[1] ?? '0'),
      }))
      .filter(d => d.rps > 0.001)
      .sort((a, b) => b.rps - a.rps);
  }, [depsData]);

  // Pod status counts
  const podCounts = React.useMemo(() => {
    const counts = { Running: 0, Pending: 0, Failed: 0 };
    podStatusData.forEach(r => {
      const phase = r.metric.phase as keyof typeof counts;
      const val = parseFloat(r.value?.[1] ?? '0');
      if (val > 0 && phase in counts) {
        counts[phase] += val;
      }
    });
    return counts;
  }, [podStatusData]);

  // Infrastructure CPU timeline data
  const infraCpuData: TimeSeriesPoint[] = React.useMemo(() => {
    if (infraCpuTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    infraCpuTimeline.forEach(r => {
      const pod = r.metric.pod || 'unknown';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![pod] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [infraCpuTimeline]);

  // Infrastructure Memory timeline data
  const infraMemData: TimeSeriesPoint[] = React.useMemo(() => {
    if (infraMemTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    infraMemTimeline.forEach(r => {
      const pod = r.metric.pod || 'unknown';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![pod] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [infraMemTimeline]);

  // Infrastructure Network Rx timeline data
  const infraNetRxData: TimeSeriesPoint[] = React.useMemo(() => {
    if (infraNetRxTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    infraNetRxTimeline.forEach(r => {
      const pod = r.metric.pod || 'unknown';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![pod] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [infraNetRxTimeline]);

  // Infrastructure Network Tx timeline data
  const infraNetTxData: TimeSeriesPoint[] = React.useMemo(() => {
    if (infraNetTxTimeline.length === 0) return [];
    const timeMap = new Map<number, TimeSeriesPoint>();
    infraNetTxTimeline.forEach(r => {
      const pod = r.metric.pod || 'unknown';
      (r.values ?? []).forEach(([ts, val]) => {
        if (!timeMap.has(ts)) timeMap.set(ts, { time: fmtTime(ts), ts });
        timeMap.get(ts)![pod] = parseFloat(val);
      });
    });
    return Array.from(timeMap.values()).sort((a, b) => a.ts - b.ts);
  }, [infraNetTxTimeline]);

  // Pod names for infrastructure charts
  const infraPodNames = React.useMemo(() => {
    return infraCpuTimeline.map(r => r.metric.pod || 'unknown');
  }, [infraCpuTimeline]);

  const infraNetRxPodNames = React.useMemo(() => {
    return infraNetRxTimeline.map(r => r.metric.pod || 'unknown');
  }, [infraNetRxTimeline]);

  const infraNetTxPodNames = React.useMemo(() => {
    return infraNetTxTimeline.map(r => r.metric.pod || 'unknown');
  }, [infraNetTxTimeline]);

  if (!ns) {
    return (
      <EmptyState
        title="No namespace configured"
        missing="info"
        description="backstage.io/kubernetes-namespace annotation is required."
      />
    );
  }

  const loading = rpsLoading || timelineLoading || xviewLoading;

  return (
    <Grid container spacing={2} direction="column">
      {/* Controls */}
      <Grid item>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              select
              label="Lookback"
              value={lookback}
              onChange={e => setLookback(e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: 130 }}
            >
              {LOOKBACK_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setLookback(prev => prev)}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              namespace: <strong>{ns}</strong>
              {isSystem && !ns.endsWith('-dev') && ' + dev'}{' '}
              {serviceName && `| entity: ${serviceName}`}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      {loading && (
        <Grid item>
          <LinearProgress />
        </Grid>
      )}

      {/* RED Summary Cards */}
      <Grid item>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card className={classes.metricCard} variant="outlined">
              <CardContent>
                <Typography className={classes.metricValue}>
                  {fmtRps(totalRps)}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Requests/sec
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.metricCard} variant="outlined">
              <CardContent>
                <Typography
                  className={`${classes.metricValue} ${
                    avgErrorRate > 5
                      ? classes.errorText
                      : avgErrorRate > 1
                        ? classes.warnText
                        : classes.successText
                  }`}
                >
                  {fmtPercent(avgErrorRate)}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Error Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.metricCard} variant="outlined">
              <CardContent>
                <Typography
                  className={`${classes.metricValue} ${
                    avgP95 > 1000
                      ? classes.errorText
                      : avgP95 > 500
                        ? classes.warnText
                        : classes.successText
                  }`}
                >
                  {fmtDuration(avgP95)}
                </Typography>
                <Typography className={classes.metricLabel}>
                  P95 Latency
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* X-View: Transaction Scatter Plot */}
      {xviewData.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            X-View — Transaction Response Time ({xviewData.length} spans)
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(v: number) => fmtTime(v)}
                    tick={{ fontSize: 11 }}
                    name="Time"
                  />
                  <YAxis
                    dataKey="duration"
                    type="number"
                    tick={{ fontSize: 11 }}
                    name="Duration (ms)"
                    label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                  />
                  <ZAxis range={[20, 20]} />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'Time') return fmtTime(value);
                      if (name === 'Duration (ms)') return fmtDuration(value);
                      return value;
                    }}
                    labelFormatter={() => ''}
                    content={({ payload }: any) => {
                      if (!payload?.[0]) return null;
                      const d = payload[0].payload as XViewPoint;
                      return (
                        <div style={{ background: '#fff', border: '1px solid #ccc', padding: 8, borderRadius: 4, fontSize: 12 }}>
                          <div><strong>{d.spanName}</strong></div>
                          <div>Duration: {fmtDuration(d.duration)}</div>
                          <div>Time: {new Date(d.time * 1000).toLocaleTimeString('ko-KR')}</div>
                          <div style={{ color: d.status === 'error' ? '#d32f2f' : '#4caf50' }}>
                            {d.status === 'error' ? 'ERROR' : 'OK'}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Scatter name="OK" data={xviewOk} fill="#4caf50" opacity={0.6} />
                  <Scatter name="Error" data={xviewErr} fill="#d32f2f" opacity={0.8} />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* RPS Timeline */}
      {timelineData.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            Request Rate (RPS)
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {serviceNames.map((svc, i) => (
                    <Line
                      key={svc}
                      type="monotone"
                      dataKey={`rps_${svc}`}
                      name={svc}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* P95 Latency Timeline */}
      {latencyTimelineData.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            P95 Latency (ms)
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={latencyTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {serviceNames.map((svc, i) => (
                    <Line
                      key={svc}
                      type="monotone"
                      dataKey={`p95_${svc}`}
                      name={`${svc} p95`}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Error Rate Timeline */}
      {errorTimelineData.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            Error Rate (req/s)
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={errorTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {serviceNames.map((svc, i) => (
                    <Line
                      key={svc}
                      type="monotone"
                      dataKey={`err_${svc}`}
                      name={`${svc} errors`}
                      stroke={COLORS[(i + 1) % COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* JVM Thread States */}
      {threadData.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            JVM Threads
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={threadData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="RUNNABLE" name="Active" stroke="#4caf50" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="TIMED_WAITING" name="Timed Wait" stroke="#ff9800" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="WAITING" name="Waiting" stroke="#2196f3" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="BLOCKED" name="Blocked" stroke="#d32f2f" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* JVM Heap Memory + CPU/GC */}
      {(heapData.length > 0 || jvmMiscData.length > 0) && (
        <Grid item>
          <Grid container spacing={2}>
            {heapData.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography className={classes.sectionTitle}>
                  JVM Heap Memory (MB)
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={heapData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="heap" name="Used" stroke="#1976d2" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {jvmMiscData.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography className={classes.sectionTitle}>
                  JVM CPU (%) / GC Pause (ms)
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={jvmMiscData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="cpuPct" name="CPU %" stroke="#1976d2" strokeWidth={2} dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="gcMs" name="GC ms/s" stroke="#ff9800" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {/* Infrastructure Metrics */}
      {!isSystem && (podCounts.Running > 0 || podCounts.Pending > 0 || podCounts.Failed > 0 || infraCpuData.length > 0 || infraMemData.length > 0) && (
        <>
          <Grid item>
            <Typography className={classes.sectionTitle}>
              Infrastructure
            </Typography>
          </Grid>

          {/* Pod Status */}
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card className={classes.metricCard} variant="outlined">
                  <CardContent>
                    <Typography className={`${classes.metricValue} ${classes.successText}`}>
                      {podCounts.Running}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Running
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card className={classes.metricCard} variant="outlined">
                  <CardContent>
                    <Typography className={`${classes.metricValue} ${podCounts.Pending > 0 ? classes.warnText : ''}`}>
                      {podCounts.Pending}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card className={classes.metricCard} variant="outlined">
                  <CardContent>
                    <Typography className={`${classes.metricValue} ${podCounts.Failed > 0 ? classes.errorText : ''}`}>
                      {podCounts.Failed}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Failed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* CPU + Memory side by side */}
          {(infraCpuData.length > 0 || infraMemData.length > 0) && (
            <Grid item>
              <Grid container spacing={2}>
                {infraCpuData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>
                      CPU Usage (cores)
                    </Typography>
                    <Card variant="outlined">
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={infraCpuData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend />
                            {infraPodNames.map((pod, i) => (
                              <Line
                                key={pod}
                                type="monotone"
                                dataKey={pod}
                                name={pod}
                                stroke={COLORS[i % COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {infraMemData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>
                      Memory Usage (MB)
                    </Typography>
                    <Card variant="outlined">
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={infraMemData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend />
                            {infraPodNames.map((pod, i) => (
                              <Line
                                key={pod}
                                type="monotone"
                                dataKey={pod}
                                name={pod}
                                stroke={COLORS[i % COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          {/* Network Rx + Tx side by side */}
          {(infraNetRxData.length > 0 || infraNetTxData.length > 0) && (
            <Grid item>
              <Grid container spacing={2}>
                {infraNetRxData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>
                      Network Receive (bytes/sec)
                    </Typography>
                    <Card variant="outlined">
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={infraNetRxData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend />
                            {infraNetRxPodNames.map((pod, i) => (
                              <Line
                                key={pod}
                                type="monotone"
                                dataKey={pod}
                                name={pod}
                                stroke={COLORS[i % COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {infraNetTxData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>
                      Network Transmit (bytes/sec)
                    </Typography>
                    <Card variant="outlined">
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={infraNetTxData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend />
                            {infraNetTxPodNames.map((pod, i) => (
                              <Line
                                key={pod}
                                type="monotone"
                                dataKey={pod}
                                name={pod}
                                stroke={COLORS[i % COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Per-API Summary Table */}
      {apiSummaries.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            API Endpoints ({apiSummaries.length})
          </Typography>
          <Card variant="outlined">
            <CardContent style={{ padding: 0 }}>
              {/* Bar chart: top 10 by RPS */}
              <Box p={2}>
                <ResponsiveContainer width="100%" height={Math.min(apiSummaries.length * 32 + 40, 400)}>
                  <BarChart
                    data={apiSummaries.slice(0, 15)}
                    layout="vertical"
                    margin={{ left: 120, right: 20, top: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="spanName"
                      tick={{ fontSize: 11 }}
                      width={110}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        if (name === 'p95') return fmtDuration(value);
                        if (name === 'errorRate') return fmtPercent(value);
                        return fmtRps(value);
                      }}
                    />
                    <Legend />
                    <Bar dataKey="p95" name="p95" fill="#1976d2" barSize={12}>
                      {apiSummaries.slice(0, 15).map((entry, idx) => (
                        <Cell
                          key={idx}
                          fill={
                            entry.p95 > 1000
                              ? '#d32f2f'
                              : entry.p95 > 500
                                ? '#ff9800'
                                : '#1976d2'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              {/* Table */}
              <Box px={2} pb={2} style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.85rem',
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: '2px solid #e0e0e0',
                        textAlign: 'left',
                      }}
                    >
                      <th style={{ padding: '8px 12px' }}>Endpoint</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>
                        RPS
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>
                        Error %
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>
                        P50
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>
                        P95
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>
                        P99
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiSummaries.map(s => (
                      <tr
                        key={s.spanName}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td style={{ padding: '6px 12px' }}>
                          <code style={{ fontSize: '0.82rem' }}>
                            {s.spanName}
                          </code>
                        </td>
                        <td
                          style={{ padding: '6px 12px', textAlign: 'right' }}
                        >
                          {fmtRps(s.rps)}
                        </td>
                        <td
                          style={{
                            padding: '6px 12px',
                            textAlign: 'right',
                            color:
                              s.errorRate > 5
                                ? '#d32f2f'
                                : s.errorRate > 1
                                  ? '#ff9800'
                                  : 'inherit',
                          }}
                        >
                          {fmtPercent(s.errorRate)}
                        </td>
                        <td
                          style={{ padding: '6px 12px', textAlign: 'right' }}
                        >
                          {fmtDuration(s.p50)}
                        </td>
                        <td
                          style={{
                            padding: '6px 12px',
                            textAlign: 'right',
                            fontWeight: s.p95 > 500 ? 700 : 400,
                            color: s.p95 > 1000 ? '#d32f2f' : 'inherit',
                          }}
                        >
                          {fmtDuration(s.p95)}
                        </td>
                        <td
                          style={{
                            padding: '6px 12px',
                            textAlign: 'right',
                            color: s.p99 > 1000 ? '#d32f2f' : 'inherit',
                          }}
                        >
                          {fmtDuration(s.p99)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Service Dependencies */}
      {deps.length > 0 && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            Service Dependencies
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={1}>
                {deps.map((d, i) => (
                  <Grid item key={i}>
                    <Chip
                      label={`${d.client} → ${d.server} (${fmtRps(d.rps)} rps)`}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Empty state */}
      {!loading && totalRps === 0 && apiSummaries.length === 0 && (
        <Grid item>
          <EmptyState
            title="No span metrics available"
            missing="data"
            description={`No traces_spanmetrics data found for namespace "${ns}". Ensure services have OTel instrumentation enabled and are receiving traffic.`}
          />
        </Grid>
      )}
    </Grid>
  );
};
